"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import Icon from "../../components/Icon";
import { EsqueletoLista } from "../../components/Esqueleto";
import { api, hasSession, isAuthError } from "../../lib/api";

interface RequestRow {
  id: string; code: string; status: string; mode: string; createdAt: string;
  deviceBrand: string; deviceModel: string;
  tech: { displayName: string };
  quote?: { amountGs: number; acceptedAt?: string | null } | null;
}

const gs = (n: number) => `₲ ${n.toLocaleString("es-PY")}`;
const ESTADO: Record<string, { label: string; color: string }> = {
  OPEN: { label: "Esperando presupuesto", color: "#E8A23C" },
  QUOTED: { label: "Presupuesto listo", color: "#72A9D8" },
  ACCEPTED: { label: "Aceptado", color: "#5CBE8F" },
  IN_PROGRESS: { label: "En reparación", color: "#E8A23C" },
  READY: { label: "Listo para retirar", color: "#5CBE8F" },
  DELIVERED: { label: "Entregado", color: "#5CBE8F" },
  CANCELLED: { label: "Cancelada", color: "#DE705C" },
};

export default function MisSolicitudes() {
  const router = useRouter();
  const [items, setItems] = useState<RequestRow[] | null>(null);
  const [error, setError] = useState("");

  const cargar = useCallback(() => {
    setError("");
    api<{ asClient: RequestRow[] }>("/requests")
      .then((r) => setItems(r.asClient))
      .catch((e) => {
        if (isAuthError(e)) router.push("/ingresar");
        else { setError(e instanceof Error ? e.message : "No pudimos traer tus reparaciones"); setItems([]); }
      });
  }, [router]);

  useEffect(() => {
    if (!hasSession()) { router.push("/ingresar"); return; }
    cargar();
  }, [cargar, router]);

  return (
    <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] p-6 pb-24">
      <div className="max-w-2xl mx-auto flex flex-col gap-4">
        <header className="flex items-center gap-3">
          <Link href="/perfil" aria-label="Volver" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-[#B5C0BB] hover:bg-white/5"><Icon name="chevron-left" size={20} /></Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Mis reparaciones</h1>
            <p className="text-xs text-[#808D88]">solicitudes que hiciste a técnicos</p>
          </div>
        </header>

        {error && (
          <div className="text-sm text-[#DE705C] bg-[#DE705C]/10 rounded-lg px-4 py-2.5 flex items-center justify-between gap-3">
            <span>{error}</span>
            <button onClick={() => { setItems(null); cargar(); }}
              className="shrink-0 rounded-lg border border-[#DE705C]/40 px-3 py-1.5 text-[13px] font-semibold hover:bg-[#DE705C]/10">
              Reintentar
            </button>
          </div>
        )}

        {items === null ? (
          <EsqueletoLista filas={4} />
        ) : items.length === 0 ? (
          <div className="rounded-xl bg-[#1A2123] border border-white/10 p-8 text-center text-[#808D88]">
            Todavía no pediste ninguna reparación.{" "}
            <Link href="/buscar" className="text-[#E8A23C] font-semibold">Buscar un técnico</Link>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {items.map((r) => {
              const est = ESTADO[r.status] ?? { label: r.status, color: "#808D88" };
              return (
                <Link key={r.id} href={`/r/${r.id}`}
                  className="rounded-xl bg-[#1A2123] border border-white/10 p-4 hover:bg-[#202829] transition block">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <span className="font-mono text-xs text-[#808D88]">{r.code} · {r.tech.displayName}</span>
                      <strong className="block text-[15px]">{r.deviceBrand} {r.deviceModel}</strong>
                      <span className="text-xs text-[#808D88]">
                        <span className="inline-flex items-center gap-1.5 align-middle">
                          <Icon name={r.mode === "SHOP" ? "store" : "home"} size={15} />
                          {r.mode === "SHOP" ? "en local" : "a domicilio"}
                        </span> · {new Date(r.createdAt).toLocaleDateString("es-PY")}
                      </span>
                    </div>
                    <div className="text-right flex-none">
                      <span className="text-[11px] font-semibold rounded-full px-2.5 py-1"
                        style={{ background: `${est.color}22`, color: est.color }}>{est.label}</span>
                      {r.quote && <div className="font-mono text-[13px] text-[#B5C0BB] mt-1">{gs(r.quote.amountGs)}</div>}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
